import React, { useMemo, useState } from "react";
import {
  FaArrowRight,
  FaCheck,
  FaExternalLinkAlt,
  FaHeart,
} from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";

import {
  JUDO_PRESENCE_PLANS,
  JUDO_PRESENCE_PRO,
} from "../data/judoPresenceProData";

import { cn } from "../utils/judoPresenceProUtils";

function PlanNavigation({
  selectedPlanId,
  onChange,
}) {
  return (
    <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
      <div className="flex min-w-max border-b border-slate-200 dark:border-white/10">
        {JUDO_PRESENCE_PLANS.map((plan) => {
          const selected = plan.id === selectedPlanId;

          return (
            <button
              key={plan.id}
              type="button"
              onClick={() => onChange(plan.id)}
              className={cn(
                "group relative min-w-[190px] px-5 pb-5 pt-2 text-left",
                "transition-colors first:pl-0",
                selected
                  ? "text-[#0868f2] dark:text-blue-300"
                  : "text-slate-500 hover:text-slate-950 dark:text-slate-400 dark:hover:text-white",
              )}
            >
              <span className="flex items-center gap-2">
                <span className="text-sm font-semibold">
                  {plan.name}
                </span>

                {plan.recommended ? (
                  <span className="rounded-full bg-orange-50 px-2 py-1 text-[8px] font-semibold uppercase tracking-[0.12em] text-[#ff7900] dark:bg-orange-500/10">
                    Conseillé
                  </span>
                ) : null}
              </span>

              <span className="mt-1 block text-xs">
                {plan.priceText}
                {plan.suffix}
              </span>

              <span
                className={cn(
                  "absolute inset-x-5 -bottom-px h-[2px] bg-[#ff7900] transition-transform duration-300 first:left-0",
                  selected
                    ? "scale-x-100"
                    : "scale-x-0 group-hover:scale-x-100",
                )}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function JudoPresenceProPricing() {
  const [selectedPlanId, setSelectedPlanId] =
    useState("presence-pro");

  const selectedPlan = useMemo(() => {
    return (
      JUDO_PRESENCE_PLANS.find(
        (plan) => plan.id === selectedPlanId,
      ) || JUDO_PRESENCE_PLANS[0]
    );
  }, [selectedPlanId]);

  return (
    <section
      id="presence-pricing"
      className="scroll-mt-24 bg-white py-20 dark:bg-slate-950 sm:py-24 lg:py-28"
    >
      <div className="mx-auto max-w-[1320px] px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="grid gap-8 border-b border-slate-200 pb-10 dark:border-white/10 lg:grid-cols-[1fr_0.85fr] lg:items-end">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#0868f2] dark:text-blue-300">
                Formules Judo Présence Pro
              </p>

              <h2 className="mt-5 text-4xl font-semibold leading-[1.08] tracking-[-0.04em] text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
                Choisissez seulement
                <span className="block text-[#0868f2] dark:text-blue-300">
                  la capacité nécessaire.
                </span>
              </h2>
            </div>

            <p className="max-w-xl text-base leading-8 text-slate-600 dark:text-slate-300 lg:justify-self-end">
              Les fonctions restent simples. La formule dépend
              principalement du nombre de dojos et d’adhérents à gérer.
            </p>
          </div>
        </Reveal>

        <Reveal className="mt-10">
          <PlanNavigation
            selectedPlanId={selectedPlanId}
            onChange={setSelectedPlanId}
          />
        </Reveal>

        <Reveal className="mt-10">
          <div
            key={selectedPlan.id}
            className="grid gap-10 rounded-[26px] border border-slate-200 bg-[#f7f9f8] p-6 dark:border-white/10 dark:bg-slate-900 sm:p-8 lg:grid-cols-[0.78fr_1.22fr] lg:p-10"
          >
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#ff7900]">
                {selectedPlan.eyebrow}
              </p>

              <h3 className="mt-4 text-4xl font-semibold tracking-[-0.04em] text-slate-950 dark:text-white">
                {selectedPlan.name}
              </h3>

              <p className="mt-4 text-base leading-8 text-slate-600 dark:text-slate-300">
                {selectedPlan.description}
              </p>

              <div className="mt-7 flex items-end gap-2">
                <span className="text-5xl font-semibold tracking-[-0.05em] text-[#0868f2] dark:text-blue-300">
                  {selectedPlan.priceText}
                </span>

                {selectedPlan.suffix ? (
                  <span className="pb-1.5 text-sm font-medium text-slate-500 dark:text-slate-400">
                    {selectedPlan.suffix}
                  </span>
                ) : null}
              </div>

              <p className="mt-4 inline-flex rounded-full bg-blue-50 px-4 py-2 text-xs font-semibold text-[#0868f2] dark:bg-blue-500/10 dark:text-blue-300">
                {selectedPlan.capacity}
              </p>

              <a
                href={JUDO_PRESENCE_PRO.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group mt-8 flex h-12 w-fit items-center justify-center gap-3 rounded-full bg-[#0868f2] px-6 text-sm font-semibold text-white transition hover:bg-[#0648b8]"
              >
                {selectedPlan.cta}

                <span className="grid h-7 w-7 place-items-center rounded-full bg-[#ff7900]">
                  <FaExternalLinkAlt className="text-[9px]" />
                </span>
              </a>
            </div>

            <div className="border-t border-slate-200 pt-7 dark:border-white/10 lg:border-l lg:border-t-0 lg:pl-10 lg:pt-0">
              <p className="mb-5 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                Ce qui est inclus
              </p>

              <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
                {selectedPlan.features.map((feature) => (
                  <div
                    key={feature}
                    className="flex items-start gap-3 text-sm leading-6 text-slate-600 dark:text-slate-300"
                  >
                    <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-orange-50 text-[#ff7900] dark:bg-orange-500/10">
                      <FaCheck className="text-[8px]" />
                    </span>

                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal className="mt-10">
          <div className="flex flex-col gap-6 border-y border-slate-200 py-7 dark:border-white/10 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-orange-50 text-[#ff7900] dark:bg-orange-500/10">
                <FaHeart />
              </span>

              <div>
                <h3 className="font-semibold text-slate-950 dark:text-white">
                  Soutenir le développement de l’application
                </h3>

                <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500 dark:text-slate-400">
                  Un don facultatif permet de financer les nouvelles
                  fonctionnalités et les améliorations de Judo Présence Pro.
                </p>
              </div>
            </div>

            <a
              href={JUDO_PRESENCE_PRO.donationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex h-11 shrink-0 items-center justify-center gap-3 self-start rounded-full border border-slate-300 px-5 text-sm font-semibold text-slate-800 transition hover:border-[#ff7900] hover:text-[#ff7900] dark:border-white/15 dark:text-white lg:self-auto"
            >
              Faire un don de 5 €

              <FaArrowRight className="text-[10px] transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}